// pages/DetalleFactura.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  obtenerFactura, 
  emitirFactura, 
  anularFactura,
  obtenerPagosFactura,
  registrarPago 
} from '../services/facturaService';
import api from '../services/api';
import { formatearFecha, obtenerFechaLocal } from '../utils/dateUtils';
import '../styles/DetalleFactura.css';

const DetalleFactura = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [factura, setFactura] = useState(null);
  const [pagos, setPagos] = useState([]);
  const [config, setConfig] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);   // logo de la empresa contable emisora
  const [loading, setLoading] = useState(true);
  const [mostrarModalPago, setMostrarModalPago] = useState(false);
  
  const [nuevoPago, setNuevoPago] = useState({
    monto: '',
    fecha_pago: obtenerFechaLocal(),
    metodo_pago: 'transferencia',
    referencia: '',
    banco: '',
    observaciones: ''
  });

  useEffect(() => {
    cargarDatos();
  }, [id]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [dataFactura, dataPagos, dataConfig] = await Promise.all([
        obtenerFactura(id),
        obtenerPagosFactura(id),
        api.get('/configuracion')
      ]);
      setFactura(dataFactura);
      setPagos(dataPagos.pagos || []);
      setConfig(dataConfig.data);

      // Logo de la empresa contable emisora (aparece en la impresión)
      if (dataFactura?.empresa_id) {
        try {
          const emp = await api.get(`/empresas-contables/${dataFactura.empresa_id}`);
          if (emp.data?.logo_path) {
            const v = emp.data.logo_actualizado
              ? new Date(emp.data.logo_actualizado).getTime() : Date.now();
            setLogoUrl(`/uploads/${emp.data.logo_path}?v=${v}`);
          }
        } catch (_) { /* sin logo → imprime sin él */ }
      }
    } catch (error) {
      console.error('Error cargando factura:', error);
      alert('Error al cargar factura');
      navigate('/facturas');
    } finally {
      setLoading(false);
    }
  };

  const handleEmitir = async () => {
    let mensaje = '¿Emitir esta factura? No podrás editarla después.';
    
    if (!factura.ncf) {
      mensaje = '⚠️ Esta factura NO tiene NCF asignado.\n\n' +
                'Se emitirá como factura interna sin valor fiscal.\n\n' +
                '¿Deseas continuar?';
    }
    
    if (!window.confirm(mensaje)) {
      return;
    }

    try {
      await emitirFactura(id);
      alert('✅ Factura emitida correctamente');
      cargarDatos();
    } catch (error) {
      console.error('Error emitiendo factura:', error);
      alert(error.response?.data?.error || 'Error al emitir factura');
    }
  };

 const handleAnular = async () => {
  const motivo = prompt('Ingresa el motivo de anulación:');
  if (!motivo) return;

  if (!window.confirm('¿Anular esta factura? Esta acción no se puede deshacer.')) {
    return;
  }

  try {
    const response = await anularFactura(id, motivo);
    
    let mensaje = '✅ Factura anulada correctamente';
    
    // Si venía de certificación, informar que regresó a aprobada
    if (response.certificacion_actualizada) {
      mensaje += '\n\n📋 La certificación asociada ha regresado a estado "Aprobada".\nPuedes generar una nueva factura desde la certificación.';
    }
    
    alert(mensaje);
    cargarDatos();
  } catch (error) {
    console.error('Error anulando factura:', error);
    alert(error.response?.data?.error || 'Error al anular factura');
  }
};

  const handleRegistrarPago = async (e) => {
    e.preventDefault();
    
    if (parseFloat(nuevoPago.monto) <= 0) {
      alert('El monto debe ser mayor a 0');
      return;
    }

    if (parseFloat(nuevoPago.monto) > factura.saldoPendiente) {
      alert(`El monto no puede ser mayor al saldo pendiente (${formatearMonto(factura.saldoPendiente)})`);
      return;
    }

    try {
      await registrarPago({
        FacturaId: parseInt(id),
        ...nuevoPago,
        monto: parseFloat(nuevoPago.monto)
      });
      
      alert('✅ Pago registrado correctamente');
      setMostrarModalPago(false);
      setNuevoPago({
        monto: '',
        fecha_pago: obtenerFechaLocal(),
        metodo_pago: 'transferencia',
        referencia: '',
        banco: '',
        observaciones: ''
      });
      cargarDatos();
    } catch (error) {
      console.error('Error registrando pago:', error);
      alert(error.response?.data?.error || 'Error al registrar pago');
    }
  };

  const formatearMonto = (monto) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: config?.fiscal?.moneda?.codigo || 'DOP'
    }).format(monto);
  };

  const getEstadoBadge = (estado) => {
    const badges = {
      borrador: { color: '#6c757d', label: 'BORRADOR' },
      emitida: { color: '#0066cc', label: 'EMITIDA' },
      parcial: { color: '#f59e0b', label: 'PAGO PARCIAL' },
      pagada: { color: '#28a745', label: 'PAGADA' },
      vencida: { color: '#dc3545', label: 'VENCIDA' },
      anulada: { color: '#343a40', label: 'ANULADA' }
    };
    return badges[estado] || badges.borrador;
  };

  const getMetodoPagoLabel = (metodo) => {
    const labels = {
      efectivo: '💵 Efectivo',
      transferencia: '🏦 Transferencia',
      cheque: '📝 Cheque',
      tarjeta: '💳 Tarjeta'
    };
    return labels[metodo] || metodo;
  };

  if (loading) {
    return (
      <div className="detalle-container">
        <div className="loading">Cargando factura...</div>
      </div>
    );
  }

  if (!factura) {
    return (
      <div className="detalle-container">
        <div className="error">Factura no encontrada</div>
      </div>
    );
  }

  const estadoBadge = getEstadoBadge(factura.estado);

  return (
    <>
      <div className="detalle-container" id="printArea">
        {/* Header - Solo en pantalla */}
        <div className="detalle-header no-print">
          <div className="detalle-header-left">
            <button 
              className="btn btn-secondary"
              onClick={() => navigate('/facturas')}
            >
              ← Volver
            </button>
            <div className="detalle-title-section">
              <h1>Factura {factura.numero_factura}</h1>
              <span 
                className="estado-badge"
                style={{ background: estadoBadge.color }}
              >
                {estadoBadge.label}
              </span>
           </div>
        </div>

        {/* Alerta si viene de certificación */}
        {factura.CertificacionId && (
          <div className="alerta-certificacion">
            <div className="alerta-icon">⚠️</div>
            <div className="alerta-content">
              <strong>Factura generada desde Certificación</strong>
              <p>Esta factura NO puede editarse porque fue generada automáticamente desde una certificación de obra. Si necesitas corregir algo, anula esta factura y genera una nueva desde la certificación.</p>
              {factura.certificacion && (
                <button 
                  className="btn-link-certificacion"
                  onClick={() => navigate(`/certificaciones/${factura.CertificacionId}`)}
                >
                  👁️ Ver Certificación #{factura.certificacion.numero_certificacion}
                </button>
              )}
            </div>
          </div>
        )}

        <div className="detalle-actions">
           {factura.estado === 'borrador' && (
  <>
  {!factura.CertificacionId && (
    <button 
     className="btn btn-warning"
     onClick={() => navigate(`/facturas/${id}/editar`)}
  >
   ✏️ Editar
     </button>
  )}
                <button className="btn btn-success" onClick={handleEmitir}>
                  📤 Emitir Factura
                </button>
                <button className="btn btn-danger" onClick={handleAnular}>
                  ❌ Anular
                </button>
              </>
            )}
            
            {['emitida', 'parcial', 'vencida'].includes(factura.estado) && (
              <>
                <button 
                  className="btn btn-primary"
                  onClick={() => setMostrarModalPago(true)}
                >
                  💰 Registrar Pago
                </button>
                <button className="btn btn-danger" onClick={handleAnular}>
                  ❌ Anular
                </button>
              </>
            )}

            <button className="btn btn-secondary" onClick={() => window.print()}>
              🖨️ Imprimir
            </button>
          </div>
        </div>

        {/* VERSIÓN DE IMPRESIÓN - Formato profesional */}
        <div className="print-only">
          {/* Header de factura profesional */}
          <div className="print-header">
            <div className="print-empresa">
              {logoUrl && (
                <img src={logoUrl} alt="Logo"
                     style={{ maxHeight: 70, maxWidth: 200, marginBottom: 8, display: 'block' }} />
              )}
              <h1>{config?.empresa_nombre || 'Mi Empresa'}</h1>
              <p><strong>RNC:</strong> {config?.empresa_rnc || 'N/A'}</p>
              <p><strong>Tel:</strong> {config?.empresa_telefono || 'N/A'}</p>
              <p><strong>Email:</strong> {config?.empresa_email || 'N/A'}</p>
              <p>{config?.empresa_direccion || ''}</p>
            </div>
            <div className="print-factura-info">
              <h2 className="print-factura-numero">FACTURA</h2>
              <p style={{ fontSize: '20px', fontWeight: 'bold', margin: '5px 0' }}>
                {factura.numero_factura}
              </p>
              <span 
                className="print-factura-estado"
                style={{ background: estadoBadge.color, color: 'white' }}
              >
                {estadoBadge.label}
              </span>
              {factura.ncf && (
                <>
                  <p style={{ marginTop: '10px', fontSize: '11px' }}>
                    <strong>NCF:</strong> {factura.ncf}
                  </p>
                  <p style={{ fontSize: '11px' }}>
                    <strong>Tipo:</strong> {factura.tipo_ncf}
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Cliente y Proyecto */}
          <div className="print-grid">
            <div className="print-section">
              <h3>FACTURADO A:</h3>
              <p><strong>Cliente:</strong> {factura.cliente?.nombre}</p>
              {factura.cliente?.rnc_cedula && (
                <p><strong>{config?.fiscal?.id_fiscal_empresa_label || 'RNC'}/{config?.fiscal?.id_fiscal_persona_label || 'Cédula'}:</strong> {factura.cliente.rnc_cedula}</p>
              )}
              {factura.cliente?.telefono && (
                <p><strong>Teléfono:</strong> {factura.cliente.telefono}</p>
              )}
              {factura.cliente?.direccion && (
                <p><strong>Dirección:</strong> {factura.cliente.direccion}</p>
              )}
            </div>

            <div className="print-section">
              <h3>INFORMACIÓN DE FACTURA:</h3>
              <p><strong>Proyecto:</strong> {factura.proyecto?.nombre}</p>
              <p><strong>Fecha Emisión:</strong> {formatearFecha(factura.fecha_emision)}</p>
              <p><strong>Fecha Vencimiento:</strong> {formatearFecha(factura.fecha_vencimiento)}</p>
              {factura.concepto && (
                <p><strong>Concepto:</strong> {factura.concepto}</p>
              )}
            </div>
          </div>

          {/* Tabla de Items */}
          <table className="print-items-table">
            <thead>
              <tr>
                <th style={{ width: '5%' }}>#</th>
                <th style={{ width: '40%' }}>Descripción</th>
                <th style={{ width: '15%' }}>Partida</th>
                <th style={{ width: '10%' }} className="text-right">Cant.</th>
                <th style={{ width: '15%' }} className="text-right">Precio Unit.</th>
                <th style={{ width: '15%' }} className="text-right">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {factura.items?.map((item, index) => (
                <tr key={item.id}>
                  <td>{index + 1}</td>
                  <td style={{ whiteSpace: 'pre-line' }}>{item.descripcion}</td>
                  <td>{item.partida || '-'}</td>
                  <td className="text-right">{item.cantidad}</td>
                  <td className="text-right">{formatearMonto(item.precio_unitario)}</td>
                  <td className="text-right" style={{ fontWeight: '600' }}>
                    {formatearMonto(item.subtotal)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totales */}
          <div className="print-totales">
            <div className="print-totales-row">
              <span>Subtotal:</span>
              <span>{formatearMonto(factura.subtotal)}</span>
            </div>
            <div className="print-totales-row">
              <span>{config?.fiscal?.impuesto_label || 'ITBIS'} ({config?.fiscal?.impuesto_tasa ?? 18}%):</span>
              <span>{formatearMonto(factura.itbis)}</span>
            </div>
            {factura.retencion > 0 && (
              <div className="print-totales-row">
                <span>Retención:</span>
                <span style={{ color: '#dc3545' }}>-{formatearMonto(factura.retencion)}</span>
              </div>
            )}
            <div className="print-totales-row total">
              <span>TOTAL:</span>
              <span>{formatearMonto(factura.total)}</span>
            </div>
            {factura.totalPagado > 0 && (
              <>
                <div className="print-totales-row" style={{ color: '#28a745', marginTop: '10px' }}>
                  <span>Pagado:</span>
                  <span>{formatearMonto(factura.totalPagado)}</span>
                </div>
                <div className="print-totales-row" style={{ color: '#0066cc', fontWeight: 'bold' }}>
                  <span>Saldo Pendiente:</span>
                  <span>{formatearMonto(factura.saldoPendiente)}</span>
                </div>
              </>
            )}
          </div>

          {/* Historial de Pagos (si hay) */}
          {pagos.length > 0 && (
            <div style={{ marginTop: '30px', pageBreakBefore: 'avoid' }}>
              <h3 style={{ fontSize: '14px', color: '#28a745', marginBottom: '10px' }}>
                HISTORIAL DE PAGOS
              </h3>
              <table className="print-pagos-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Método</th>
                    <th>Referencia</th>
                    <th>Banco</th>
                    <th className="text-right">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {pagos.map((pago) => (
                    <tr key={pago.id}>
                      <td>{formatearFecha(pago.fecha_pago)}</td>
                      <td>{pago.metodo_pago}</td>
                      <td>{pago.referencia || '-'}</td>
                      <td>{pago.banco || '-'}</td>
                      <td className="text-right" style={{ fontWeight: '600', color: '#28a745' }}>
                        {formatearMonto(pago.monto)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Observaciones */}
          {factura.observaciones && (
            <div style={{ marginTop: '20px', padding: '10px', background: '#f9f9f9', borderRadius: '5px' }}>
              <p style={{ fontSize: '11px', margin: '0' }}>
                <strong>Observaciones:</strong> {factura.observaciones}
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="print-footer">
            <p>Esta factura fue generada electrónicamente y es válida sin firma.</p>
            <p>Para cualquier consulta, comunicarse al {config?.empresa_telefono || 'N/A'} o {config?.empresa_email || 'N/A'}</p>
          </div>
        </div>

        {/* VERSIÓN DE PANTALLA - Vista normal */}
        <div className="screen-only">
          <div className="detalle-grid">
            <div className="card">
              <h2 className="card-title">Información General</h2>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Cliente:</span>
                  <span className="info-value">{factura.cliente?.nombre}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Proyecto:</span>
                  <span className="info-value">{factura.proyecto?.nombre}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Fecha Emisión:</span>
                  <span className="info-value">{formatearFecha(factura.fecha_emision)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Fecha Vencimiento:</span>
                  <span className="info-value">{formatearFecha(factura.fecha_vencimiento)}</span>
                </div>
                {factura.tipo_ncf && (
                  <>
                    <div className="info-item">
                      <span className="info-label">Tipo NCF:</span>
                      <span className="info-value">{factura.tipo_ncf}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">NCF:</span>
                      <span className="info-value">{factura.ncf || 'Sin asignar'}</span>
                    </div>
                  </>
                )}
                {factura.concepto && (
                  <div className="info-item full-width">
                    <span className="info-label">Concepto:</span>
                    <span className="info-value">{factura.concepto}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="card">
              <h2 className="card-title">Resumen Financiero</h2>
              <div className="resumen-financiero">
                <div className="resumen-row">
                  <span>Subtotal:</span>
                  <span>{formatearMonto(factura.subtotal)}</span>
                </div>
                <div className="resumen-row">
                  <span>{config?.fiscal?.impuesto_label || 'ITBIS'} ({config?.fiscal?.impuesto_tasa ?? 18}%):</span>
                  <span>{formatearMonto(factura.itbis)}</span>
                </div>
                {factura.retencion > 0 && (
                  <div className="resumen-row">
                    <span>Retención:</span>
                    <span className="text-danger">-{formatearMonto(factura.retencion)}</span>
                  </div>
                )}
                <div className="resumen-row resumen-total">
                  <span>TOTAL:</span>
                  <span>{formatearMonto(factura.total)}</span>
                </div>
                <div className="resumen-row text-success">
                  <span>Total Pagado:</span>
                  <span>{formatearMonto(factura.totalPagado || 0)}</span>
                </div>
                <div className="resumen-row text-primary">
                  <span>Saldo Pendiente:</span>
                  <span className="font-bold">{formatearMonto(factura.saldoPendiente || 0)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <h2 className="card-title">Items de Factura</h2>
            <div className="table-container">
              <table className="items-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Descripción</th>
                    <th>Partida</th>
                    <th className="text-right">Cantidad</th>
                    <th className="text-right">Precio Unit.</th>
                    <th className="text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {factura.items?.map((item, index) => (
                    <tr key={item.id}>
                      <td>{index + 1}</td>
                      <td style={{ whiteSpace: 'pre-line' }}>{item.descripcion}</td>
                      <td>{item.partida || '-'}</td>
                      <td className="text-right">{item.cantidad}</td>
                      <td className="text-right">{formatearMonto(item.precio_unitario)}</td>
                      <td className="text-right font-bold">{formatearMonto(item.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {pagos.length > 0 && (
            <div className="card">
              <h2 className="card-title">Historial de Pagos</h2>
              <div className="table-container">
                <table className="pagos-table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Método</th>
                      <th>Referencia</th>
                      <th>Banco</th>
                      <th className="text-right">Monto</th>
                      <th>Recibido Por</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagos.map((pago) => (
                      <tr key={pago.id}>
                        <td>{formatearFecha(pago.fecha_pago)}</td>
                        <td>{getMetodoPagoLabel(pago.metodo_pago)}</td>
                        <td>{pago.referencia || '-'}</td>
                        <td>{pago.banco || '-'}</td>
                        <td className="text-right font-bold text-success">
                          {formatearMonto(pago.monto)}
                        </td>
                        <td>{pago.receptor?.nombre || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal Registrar Pago */}
      {mostrarModalPago && (
        <div className="modal-overlay" onClick={() => setMostrarModalPago(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Registrar Pago</h2>
              <button 
                className="btn-close"
                onClick={() => setMostrarModalPago(false)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleRegistrarPago}>
              <div className="modal-body">
                <div className="modal-info">
                  <p><strong>Factura:</strong> {factura.numero_factura}</p>
                  <p><strong>Saldo Pendiente:</strong> {formatearMonto(factura.saldoPendiente)}</p>
                </div>

                <div className="form-group">
                  <label>Monto a Pagar *</label>
                  <input
                    type="number"
                    value={nuevoPago.monto}
                    onChange={(e) => setNuevoPago({...nuevoPago, monto: e.target.value})}
                    min="0.01"
                    step="0.01"
                    max={factura.saldoPendiente}
                    required
                    className="form-control"
                    placeholder="0.00"
                  />
                </div>

                <div className="form-group">
                  <label>Fecha de Pago *</label>
                  <input
                    type="date"
                    value={nuevoPago.fecha_pago}
                    onChange={(e) => setNuevoPago({...nuevoPago, fecha_pago: e.target.value})}
                    required
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label>Método de Pago *</label>
                  <select
                    value={nuevoPago.metodo_pago}
                    onChange={(e) => setNuevoPago({...nuevoPago, metodo_pago: e.target.value})}
                    required
                    className="form-control"
                  >
                    <option value="efectivo">Efectivo</option>
                    <option value="transferencia">Transferencia</option>
                    <option value="cheque">Cheque</option>
                    <option value="tarjeta">Tarjeta</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Referencia</label>
                  <input
                    type="text"
                    value={nuevoPago.referencia}
                    onChange={(e) => setNuevoPago({...nuevoPago, referencia: e.target.value})}
                    className="form-control"
                    placeholder="Ej: TRF-001"
                  />
                </div>

                <div className="form-group">
                  <label>Banco</label>
                  <input
                    type="text"
                    value={nuevoPago.banco}
                    onChange={(e) => setNuevoPago({...nuevoPago, banco: e.target.value})}
                    className="form-control"
                    placeholder="Ej: Banco Popular"
                  />
                </div>

                <div className="form-group">
                  <label>Observaciones</label>
                  <textarea
                    value={nuevoPago.observaciones}
                    onChange={(e) => setNuevoPago({...nuevoPago, observaciones: e.target.value})}
                    rows="3"
                    className="form-control"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setMostrarModalPago(false)}
                  className="btn btn-secondary"
                >
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  💰 Registrar Pago
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

const printStyles = `
  @media print {
    @page {
      margin: 10mm 12mm;
      size: letter;
    }
    
    body {
      margin: 0;
      padding: 0;
      font-size: 10px;
    }
    
    body * {
      visibility: hidden !important;
    }
    
    #printArea, #printArea * {
      visibility: visible !important;
    }
    
    #printArea {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      background: white !important;
      padding: 0;
    }
    
    .no-print, .screen-only {
      display: none !important;
    }
    
    .print-only {
      display: block !important;
    }
    
    /* Header más compacto */
    .print-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 15px;
      padding-bottom: 12px;
      border-bottom: 2px solid #0066cc;
    }
    
    .print-empresa h1 {
      font-size: 18px;
      color: #0066cc;
      margin: 0 0 3px 0;
    }
    
    .print-empresa p {
      margin: 2px 0;
      font-size: 9px;
      color: #333;
      line-height: 1.3;
    }
    
    .print-factura-numero {
      font-size: 18px;
      color: #0066cc;
      margin: 0;
    }
    
    .print-factura-info p {
      font-size: 9px;
      margin: 2px 0;
    }
    
    .print-factura-estado {
      display: inline-block;
      padding: 4px 10px;
      border-radius: 15px;
      font-size: 9px;
      font-weight: bold;
      margin-top: 5px;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    
    /* Grid más compacto */
    .print-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
      margin-bottom: 15px;
    }
    
    .print-section {
      border: 1px solid #ddd;
      padding: 8px;
      border-radius: 4px;
      page-break-inside: avoid;
    }
    
    .print-section h3 {
      font-size: 11px;
      color: #0066cc;
      margin: 0 0 5px 0;
      border-bottom: 1px solid #f0f0f0;
      padding-bottom: 3px;
    }
    
    .print-section p {
      margin: 2px 0;
      font-size: 9px;
      line-height: 1.3;
    }
    
    /* Tabla más compacta */
    .print-items-table {
      width: 100%;
      border-collapse: collapse;
      margin: 10px 0 5px 0;
      font-size: 9px;
    }
    
    .print-items-table thead {
      background: #0066cc;
      color: white;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    
    .print-items-table th {
      padding: 5px 4px;
      text-align: left;
      font-size: 9px;
      font-weight: 600;
    }
    
    .print-items-table td {
      padding: 4px;
      border-bottom: 1px solid #e0e0e0;
      font-size: 9px;
    }
    
    .print-items-table tbody tr:nth-child(even) {
      background: #f9f9f9;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    
    /* Totales más compactos */
    .print-totales {
      width: 250px;
      margin-left: auto;
      margin-top: 10px;
      border: 2px solid #0066cc;
      padding: 8px;
      border-radius: 4px;
      page-break-inside: avoid;
    }
    
    .print-totales-row {
      display: flex;
      justify-content: space-between;
      padding: 3px 0;
      font-size: 9px;
    }
    
    .print-totales-row.total {
      border-top: 2px solid #0066cc;
      margin-top: 5px;
      padding-top: 8px;
      font-size: 13px;
      font-weight: bold;
      color: #0066cc;
    }
    
    /* Tabla de pagos más compacta */
    .print-pagos-table {
      width: 100%;
      border-collapse: collapse;
      margin: 10px 0 5px 0;
      font-size: 8px;
    }
    
    .print-pagos-table thead {
      background: #28a745;
      color: white;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    
    .print-pagos-table th {
      padding: 4px;
      font-size: 8px;
    }
    
    .print-pagos-table td {
      padding: 3px 4px;
      border-bottom: 1px solid #e0e0e0;
      font-size: 8px;
    }
    
    /* Título de historial más pequeño */
    .print-only h3 {
      font-size: 11px !important;
      margin: 10px 0 5px 0 !important;
    }
    
    /* Observaciones más compactas */
    .print-only > div[style*="marginTop"] {
      margin-top: 10px !important;
      padding: 6px !important;
    }
    
    .print-only > div[style*="marginTop"] p {
      font-size: 8px !important;
    }
    
    /* Footer más compacto */
    .print-footer {
      margin-top: 15px;
      padding-top: 8px;
      border-top: 1px solid #e0e0e0;
      font-size: 7px;
      color: #666;
      text-align: center;
      line-height: 1.3;
    }
    
    .print-footer p {
      margin: 2px 0;
    }
    
    .text-right {
      text-align: right;
    }
    
    /* Forzar todo en una página */
    .print-only {
      page-break-after: avoid;
      page-break-inside: avoid;
    }
  }
  
  @media screen {
    .print-only {
      display: none;
    }
  }
`;

if (typeof document !== "undefined") {
  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.innerText = printStyles;
  document.head.appendChild(styleSheet);
}

export default DetalleFactura;